import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../services/apiBase';

const API = API_BASE;

const CUE_CONFIG = {
  trim_start:        { label: 'Trim Start',      color: '#6b7280', icon: '|<', desc: 'Start of usable audio' },
  cue_intro:         { label: 'Intro',            color: '#22c55e', icon: '>',  desc: 'Music starts here' },
  cue_voice_track:   { label: 'Voice Track',      color: '#a78bfa', icon: 'M',  desc: 'DJ voice over point' },
  cue_crossfade_in:  { label: 'Crossfade In',     color: '#38bdf8', icon: '<',  desc: 'Next track starts fading in' },
  cue_crossfade_out: { label: 'Crossfade Out',    color: '#f97316', icon: '>',  desc: 'This track fades out' },
  cue_outro:         { label: 'Outro',            color: '#f59e0b', icon: '[]', desc: 'Music starts fading' },
  trim_end:          { label: 'Trim End',         color: '#6b7280', icon: '>|', desc: 'End of usable audio' },
};

function fmt(secs) {
  if (secs == null || isNaN(secs)) return '--:--';
  const m = Math.floor(secs / 60);
  const s = (secs % 60).toFixed(1).padStart(4, '0');
  return m + ':' + s;
}

function WaveformDisplay({ samples, duration, cues, onCueSet, currentTime, trimStart, trimEnd }) {
  const canvasRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [hoveredCue, setHoveredCue] = useState(null);

  const timeToX = useCallback((t, w) => (t / duration) * w, [duration]);
  const xToTime = useCallback((x, w) => Math.max(0, Math.min(duration, (x / w) * duration)), [duration]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !samples || !samples.length || !duration) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, W, H);

    if (trimStart > 0) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, timeToX(trimStart, W), H);
    }
    if (trimEnd && trimEnd < duration) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(timeToX(trimEnd, W), 0, W, H);
    }

    const barW = W / samples.length;
    const mid = H / 2;
    samples.forEach(function(amp, i) {
      const x = i * barW;
      const h = amp * H * 0.85;
      const t = (i / samples.length) * duration;
      const played = currentTime && t <= currentTime;
      ctx.fillStyle = played ? '#22c55e' : 'rgba(56,189,248,0.5)';
      ctx.fillRect(x, mid - h / 2, Math.max(1, barW - 0.5), h);
    });

    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for (let t = 0; t <= duration; t += 30) {
      const x = timeToX(t, W);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }

    if (currentTime != null) {
      const x = timeToX(currentTime, W);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }

    Object.keys(CUE_CONFIG).forEach(function(key) {
      const cfg = CUE_CONFIG[key];
      const t = cues[key];
      if (t == null) return;
      const x = timeToX(t, W);
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = hoveredCue === key ? 3 : 2;
      ctx.setLineDash([5, 3]);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = cfg.color;
      ctx.font = 'bold 10px monospace';
      ctx.fillText(cfg.icon, x + 3, 12);
    });
  }, [samples, duration, cues, currentTime, trimStart, trimEnd, hoveredCue, timeToX]);

  const getCanvasTime = function(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return xToTime(e.clientX - rect.left, rect.width);
  };

  const getClosestCue = function(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const W = rect.width;
    let closest = null;
    let closestDist = 14;
    Object.keys(cues).forEach(function(key) {
      const val = cues[key];
      if (val == null) return;
      const dist = Math.abs(x - timeToX(val, W));
      if (dist < closestDist) { closest = key; closestDist = dist; }
    });
    return closest;
  };

  return (
    React.createElement('div', { style: { position: 'relative' } },
      React.createElement('canvas', {
        ref: canvasRef,
        width: 900,
        height: 130,
        style: { width: '100%', height: 130, display: 'block', borderRadius: 8, cursor: dragging ? 'ew-resize' : hoveredCue ? 'col-resize' : 'crosshair' },
        onClick: function(e) { if (!dragging) onCueSet('_seek', getCanvasTime(e)); },
        onMouseMove: function(e) {
          const closest = getClosestCue(e);
          setHoveredCue(closest);
          if (dragging) onCueSet(dragging, Math.round(getCanvasTime(e) * 10) / 10);
        },
        onMouseDown: function(e) { const c = getClosestCue(e); if (c) setDragging(c); },
        onMouseUp: function() { setDragging(null); },
        onMouseLeave: function() { setDragging(null); setHoveredCue(null); }
      }),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginTop: 4 } },
        [0, 0.25, 0.5, 0.75, 1].map(function(p) {
          return React.createElement('span', { key: p, style: { fontSize: 10, color: '#4b5563' } }, fmt(p * duration));
        })
      )
    )
  );
}

export default function AudioEditor() {
  const [tracks, setTracks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [waveform, setWaveform] = useState(null);
  const [cues, setCues] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const audioRef = useRef(null);
  const token = localStorage.getItem('access_token');
  const tenantId = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

  const loadTracks = useCallback(function() {
    axios.get(API + '/music/tracks', { params: { tenant_id: tenantId }, headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { setTracks(r.data || []); }).catch(console.error);
  }, [token]);

  useEffect(function() { loadTracks(); }, [loadTracks]);

  const selectTrack = async function(track) {
    setSelected(track);
    setIsPlaying(false);
    setCurrentTime(0);
    setWaveform(null);
    setMsg('');
    setDuration(track.duration_seconds || 0);
    setCues({
      trim_start: track.trim_start != null ? track.trim_start : 0,
      trim_end: track.trim_end != null ? track.trim_end : null,
      cue_intro: track.cue_intro != null ? track.cue_intro : null,
      cue_outro: track.cue_outro != null ? track.cue_outro : null,
      cue_crossfade_in: track.cue_crossfade_in != null ? track.cue_crossfade_in : null,
      cue_crossfade_out: track.cue_crossfade_out != null ? track.cue_crossfade_out : null,
      cue_voice_track: track.cue_voice_track != null ? track.cue_voice_track : null,
    });
    if (audioRef.current) {
      // Fetch with auth header and create blob URL so audio element can play
      try {
        const resp = await fetch(API + '/music/tracks/' + track.id + '/stream', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (resp.ok) {
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          audioRef.current.src = blobUrl;
          audioRef.current.load();
        }
      } catch(e) { console.error('Audio load error:', e); }
    }
    setLoading(true);
    try {
      if (track.waveform_data) {
        const data = typeof track.waveform_data === 'string' ? JSON.parse(track.waveform_data) : track.waveform_data;
        setWaveform(data);
      } else {
        const r = await axios.get(API + '/music/tracks/' + track.id + '/waveform', { headers: { Authorization: 'Bearer ' + token } });
        setWaveform(r.data.waveform);
        if (r.data.duration) setDuration(r.data.duration);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const togglePlay = function() {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); }
    else { audioRef.current.currentTime = cues.trim_start || 0; audioRef.current.play(); }
  };

  const seek = function(t) {
    if (audioRef.current) audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const setCue = function(key, val) {
    if (key === '_seek') { seek(val); return; }
    setCues(function(p) { return Object.assign({}, p, { [key]: val }); });
  };

  const saveCues = async function() {
    setSaving(true);
    try {
      await axios.patch(API + '/music/tracks/' + selected.id + '/cues', cues, { headers: { Authorization: 'Bearer ' + token } });
      setMsg('Saved!');
      setTimeout(function() { setMsg(''); }, 3000);
      loadTracks();
    } catch(e) { setMsg('Save failed: ' + (e.response && e.response.data && e.response.data.detail ? e.response.data.detail : e.message)); }
    setSaving(false);
  };

  const autoTrim = async function() {
    setTrimming(true); setMsg('Detecting silence...');
    try {
      const r = await axios.post(API + '/music/tracks/' + selected.id + '/trim-silence', {}, { headers: { Authorization: 'Bearer ' + token } });
      setCues(function(p) { return Object.assign({}, p, { trim_start: r.data.trim_start || 0, trim_end: r.data.trim_end || null }); });
      setMsg('Trimmed: start=' + fmt(r.data.trim_start) + ' end=' + fmt(r.data.trim_end));
      setTimeout(function() { setMsg(''); }, 5000);
    } catch(e) { setMsg('Trim failed'); }
    setTrimming(false);
  };

  const filtered = tracks.filter(function(t) {
    const q = search.toLowerCase();
    return !q || (t.title || '').toLowerCase().includes(q) || (t.artist || '').toLowerCase().includes(q) || (t.original_filename || '').toLowerCase().includes(q);
  });

  const tb = { width: 36, height: 38, borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#e5e7eb', cursor: 'pointer', fontSize: 15 };

  return (
    React.createElement('div', { style: { display: 'flex', height: '100vh', background: '#0a0c10', color: '#e5e7eb', fontFamily: 'system-ui,sans-serif', overflow: 'hidden' } },
      React.createElement('audio', {
        ref: audioRef,
        onTimeUpdate: function(e) { setCurrentTime(e.target.currentTime); },
        onDurationChange: function(e) { setDuration(e.target.duration); },
        onEnded: function() { setIsPlaying(false); },
        onPause: function() { setIsPlaying(false); },
        onPlay: function() { setIsPlaying(true); }
      }),
      React.createElement('div', { style: { width: 270, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' } },
        React.createElement('div', { style: { padding: '16px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' } },
          React.createElement('div', { style: { fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 10 } }, 'Audio Editor'),
          React.createElement('input', { placeholder: 'Search...', value: search, onChange: function(e) { setSearch(e.target.value); },
            style: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7, padding: '6px 10px', color: '#e5e7eb', fontSize: 12, boxSizing: 'border-box' } })
        ),
        React.createElement('div', { style: { flex: 1, overflowY: 'auto' } },
          filtered.map(function(t) {
            return React.createElement('div', {
              key: t.id,
              onClick: function() { selectTrack(t); },
              style: { padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', borderLeft: '3px solid ' + (selected && selected.id === t.id ? '#22c55e' : 'transparent'), background: selected && selected.id === t.id ? 'rgba(34,197,94,0.08)' : 'transparent' }
            },
              React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#e5e7eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                t.title || (t.original_filename ? t.original_filename.replace(/\.mp3$/i, '') : 'Untitled')),
              React.createElement('div', { style: { fontSize: 11, color: '#6b7280' } }, (t.artist || '-') + ' · ' + fmt(t.duration_seconds)),
              React.createElement('div', { style: { display: 'flex', gap: 4, marginTop: 3 } },
                t.cue_intro != null ? React.createElement('span', { style: { fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(34,197,94,0.15)', color: '#22c55e' } }, 'INTRO') : null,
                t.cue_crossfade_out != null ? React.createElement('span', { style: { fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(249,115,22,0.15)', color: '#f97316' } }, 'CF') : null,
                t.cue_voice_track != null ? React.createElement('span', { style: { fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(167,139,250,0.15)', color: '#a78bfa' } }, 'VT') : null,
                (t.trim_start > 0 || t.trim_end) ? React.createElement('span', { style: { fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(107,114,128,0.2)', color: '#9ca3af' } }, 'TRIM') : null
              )
            );
          })
        )
      ),
      React.createElement('div', { style: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } },
        !selected
          ? React.createElement('div', { style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: '#374151' } },
              React.createElement('div', { style: { fontSize: 48 } }, String.fromCharCode(127897)),
              React.createElement('div', { style: { fontSize: 15 } }, 'Select a track from the sidebar')
            )
          : React.createElement(React.Fragment, null,
              React.createElement('div', { style: { padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                React.createElement('div', null,
                  React.createElement('div', { style: { fontSize: 18, fontWeight: 800, color: '#fff' } }, selected.title || 'Untitled'),
                  React.createElement('div', { style: { fontSize: 12, color: '#6b7280', marginTop: 2 } }, (selected.artist || '') + ' · ' + fmt(duration))
                ),
                React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'center' } },
                  msg ? React.createElement('span', { style: { fontSize: 12, color: msg.startsWith('Save') ? '#22c55e' : '#f59e0b' } }, msg) : null,
                  React.createElement('button', { onClick: autoTrim, disabled: trimming, style: { padding: '7px 14px', borderRadius: 7, border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.1)', color: '#a78bfa', cursor: trimming ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600 } },
                    trimming ? 'Detecting...' : 'Auto-Trim Silence'),
                  React.createElement('button', { onClick: saveCues, disabled: saving, style: { padding: '7px 18px', borderRadius: 7, border: 'none', background: saving ? '#374151' : '#22c55e', color: '#000', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 } },
                    saving ? 'Saving...' : 'Save Cues')
                )
              ),
              React.createElement('div', { style: { padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' } },
                loading
                  ? React.createElement('div', { style: { height: 130, background: 'rgba(255,255,255,0.02)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' } }, 'Generating waveform...')
                  : waveform
                    ? React.createElement(WaveformDisplay, { samples: waveform, duration: duration, cues: cues, onCueSet: setCue, currentTime: currentTime, trimStart: cues.trim_start, trimEnd: cues.trim_end })
                    : React.createElement('div', { style: { height: 130, background: 'rgba(255,255,255,0.02)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' } }, 'No waveform data')
              ),
              React.createElement('div', { style: { padding: '10px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.01)' } },
                React.createElement('button', { onClick: function() { seek(cues.trim_start || 0); }, style: tb }, '|<'),
                React.createElement('button', { onClick: togglePlay, style: Object.assign({}, tb, { width: 44, background: isPlaying ? '#22c55e' : 'rgba(255,255,255,0.08)', color: isPlaying ? '#000' : '#fff', fontSize: 18 }) }, isPlaying ? '||' : '>'),
                React.createElement('button', { onClick: function() { seek(cues.trim_end || duration); }, style: tb }, '>|'),
                React.createElement('span', { style: { fontSize: 14, fontVariantNumeric: 'tabular-nums', color: '#22c55e', fontWeight: 700, minWidth: 55 } }, fmt(currentTime)),
                React.createElement('div', {
                  style: { flex: 1, height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, cursor: 'pointer' },
                  onClick: function(e) { const rect = e.currentTarget.getBoundingClientRect(); seek(((e.clientX - rect.left) / rect.width) * duration); }
                },
                  React.createElement('div', { style: { width: (duration ? (currentTime / duration) * 100 : 0) + '%', height: '100%', background: '#22c55e', borderRadius: 3 } })
                ),
                React.createElement('span', { style: { fontSize: 13, color: '#6b7280', minWidth: 55 } }, fmt(duration)),
                Object.keys(CUE_CONFIG).map(function(key) {
                  if (cues[key] == null) return null;
                  const cfg = CUE_CONFIG[key];
                  return React.createElement('button', { key: key, onClick: function() { seek(cues[key]); }, title: cfg.label + ' ' + fmt(cues[key]),
                    style: { padding: '3px 8px', borderRadius: 5, border: '1px solid ' + cfg.color + '40', background: cfg.color + '18', color: cfg.color, cursor: 'pointer', fontSize: 10, fontWeight: 700 } },
                    cfg.icon);
                })
              ),
              React.createElement('div', { style: { flex: 1, overflowY: 'auto', padding: '12px 24px' } },
                React.createElement('div', { style: { fontSize: 11, color: '#4b5563', marginBottom: 12 } }, 'Drag markers on waveform or click Set to place at current playback position'),
                Object.keys(CUE_CONFIG).map(function(key) {
                  const cfg = CUE_CONFIG[key];
                  return React.createElement('div', { key: key, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' } },
                    React.createElement('div', { style: { width: 3, height: 32, borderRadius: 2, background: cfg.color, flexShrink: 0 } }),
                    React.createElement('div', { style: { width: 140 } },
                      React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: cfg.color } }, cfg.icon + ' ' + cfg.label),
                      React.createElement('div', { style: { fontSize: 10, color: '#4b5563' } }, cfg.desc)
                    ),
                    React.createElement('input', { type: 'number', step: '0.1', min: '0', value: cues[key] != null ? cues[key] : '', placeholder: '--',
                      onChange: function(e) { setCue(key, e.target.value === '' ? null : parseFloat(e.target.value)); },
                      style: { width: 80, background: 'rgba(255,255,255,0.05)', border: '1px solid ' + (cues[key] != null ? cfg.color : 'rgba(255,255,255,0.08)'), borderRadius: 6, padding: '5px 8px', color: '#e5e7eb', fontSize: 13, textAlign: 'center' } }),
                    React.createElement('span', { style: { fontSize: 13, color: '#6b7280', minWidth: 54 } }, fmt(cues[key])),
                    React.createElement('button', { onClick: function() { setCue(key, Math.round(currentTime * 10) / 10); },
                      style: { padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: '#9ca3af', cursor: 'pointer', fontSize: 11 } }, 'Set'),
                    cues[key] != null ? React.createElement('button', { onClick: function() { setCue(key, null); },
                      style: { padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer', fontSize: 11 } }, 'x') : null
                  );
                }),
                React.createElement('div', { style: { marginTop: 20, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' } },
                  React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: '#4b5563', letterSpacing: 1, marginBottom: 12 } }, 'TIMING SUMMARY'),
                  React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 } },
                    [
                      ['Usable Audio', fmt((cues.trim_end || duration) - (cues.trim_start || 0))],
                      ['Intro Length', cues.cue_intro != null ? fmt(cues.cue_intro - (cues.trim_start || 0)) : '--:--'],
                      ['Outro Length', (cues.cue_outro != null) ? fmt((cues.trim_end || duration) - cues.cue_outro) : '--:--'],
                      ['CF Window', (cues.cue_crossfade_out != null && cues.cue_outro != null) ? fmt(cues.cue_outro - cues.cue_crossfade_out) : '--:--'],
                    ].map(function(item) {
                      return React.createElement('div', { key: item[0] },
                        React.createElement('div', { style: { fontSize: 10, color: '#4b5563', marginBottom: 4 } }, item[0]),
                        React.createElement('div', { style: { fontSize: 18, fontWeight: 800, color: '#e5e7eb' } }, item[1])
                      );
                    })
                  )
                )
              )
            )
      )
    )
  );
}
